import {
  forwardRef,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
} from "react";
import * as THREE from "three";
import { DesignElement } from "./types";

export interface ThreeDDesignCanvasProps {
  backgroundColor: string;
  backgroundDepth: number;
  backgroundForm: "circular" | "rectangular" | "heart" | "auto";
  backgroundEnabled?: boolean;
  backgroundWidth?: number;
  backgroundHeight?: number;
  backgroundBorderRadius?: number;
  keychainRingEnabled?: boolean;
  keychainRingSide?: "top" | "bottom" | "left" | "right";
  keychainRingEmbeddingDepth?: number; // Ratio (0-1) of tube radius embedded into model (default: 0.5 = 50%)
  keychainRingSize?: number; // Size multiplier (0.1-2.0) for ring radius (default: 1.0 = 100%)
  keychainRingThickness?: number; // Thickness multiplier (0.1-2.0) for ring tube radius (default: 1.0 = 100%)
  elements: DesignElement[];
  selectedElement: string | null;
  selectedCanvas: boolean;
  onElementSelect: (id: string | null) => void;
  onCanvasSelect: () => void;
  onElementUpdate: (id: string, updates: Partial<DesignElement>) => void;
  onElementDelete: (id: string) => void;
  onElementDoubleClick?: (id: string) => void;
}

export interface ThreeDDesignCanvasRef {
  export3DModel: () => Promise<void>;
  export3DModelOBJ: () => Promise<void>;
  captureScreenshot: () => Promise<string>; // Returns base64 data URL
}

// Constants configuration - all adjustable parameters in one place
const CANVAS_CONSTANTS = {
  // Keychain ring configuration
  keychainRing: {
    // Ring size scaling
    scaleFactorDivisor: 20, // Divisor for calculating scale factor from background dimensions
    maxRingRadius: 10, // Maximum ring radius
    ringRadiusMultiplier: 1, // Multiplier for ring radius calculation
    // Ring thickness (tube radius)
    tubeRadiusDepthRatio: 0.45, // Ratio of background depth for tube radius (40%)
    maxTubeRadius: 2, // Maximum tube radius
    // Ring embedding depth (how far inside the model)
    embeddingDepthRatio: 0.5, // 50% of tube radius embedded into model
    // Ring geometry segments
    radialSegments: 32, // Number of segments around the ring
    tubularSegments: 32, // Number of segments along the tube
    // Ring rotation
    rotationX: Math.PI / 2, // 90 degrees rotation around X axis
    // Minimum ring Y position when background is disabled
    minRingY: 5,
  },

  // Text geometry configuration
  textGeometry: {
    // Default values
    defaultFontSize: 24,
    defaultTextDepth: 5,
    // Bevel configuration for text elements
    bevelThicknessRatio: 0.1, // 10% of depth
    bevelSizeRatio: 0.05, // 5% of depth
    // Geometry segments
    curveSegments: 12,
    bevelSegments: 3,
    bevelOffset: 0,
    // Text size estimation
    textWidthMultiplier: 0.6, // Character width multiplier
    textHeightMultiplier: 1.2, // Character height multiplier
  },

  // Background geometry configuration
  backgroundGeometry: {
    // Default dimensions
    defaultWidth: 100,
    defaultHeight: 60,
    // Bevel configuration for background (auto form)
    bevelThicknessRatio: 0.05, // 5% of depth
    bevelSizeFontRatio: 0.1, // 10% of font size
    bevelSizeDepthRatio: 0.5, // 50% of depth (minimum)
    // Geometry segments
    curveSegments: 12,
    bevelSegments: 5, // More segments for smoother bevel
    bevelOffset: 0,
    roundedBoxSegments: 2, // Segments for rounded box geometry
    cylinderSegments: 32, // Segments for circular/cylinder geometry
    // Auto form fallback padding
    autoFormPadding: 0.2, // 20% padding around text bounds
  },

  // Camera configuration
  camera: {
    // Field of view
    fov: 50,
    // Near and far clipping planes
    near: 0.1,
    far: 1000,
    // Initial camera position
    initialPosition: { x: 100, y: 80, z: 100 },
    initialTarget: { x: 0, y: 0, z: 0 },
    // Camera framing
    framingDistanceMultiplier: 2.5, // Distance multiplier for framing
    framingPositionX: 0.6, // X position offset multiplier
    framingPositionY: 0.8, // Y position offset multiplier
    framingPositionZ: 0.6, // Z position offset multiplier
    // Default camera position when no objects
    defaultPosition: { x: 5, y: 5, z: 5 },
    defaultTarget: { x: 0, y: 0, z: 0 },
  },

  // Orbit controls configuration
  controls: {
    dampingFactor: 0.05,
    minDistance: 30,
    maxDistance: 500,
    initialTarget: { x: 0, y: 0, z: 0 },
  },

  // Lighting configuration
  lighting: {
    ambientLight: {
      color: 0xffffff,
      intensity: 2.0,
    },
    directionalLight: {
      color: 0xffffff,
      intensity: 1.5,
      position: { x: 80, y: 50, z: 100 },
    },
    fillLight: {
      color: 0xffffff,
      intensity: 1.0,
      position: { x: -60, y: 40, z: 60 },
    },
    topLight: {
      color: 0xffffff,
      intensity: 0.8,
      position: { x: 0, y: 100, z: 0 },
    },
  },

  // Renderer configuration
  renderer: {
    toneMappingExposure: 1.0,
  },

  // Screenshot configuration
  screenshot: {
    renderDelay: 200, // Delay in milliseconds before capturing screenshot
  },

  // Color defaults
  colors: {
    defaultBackground: "#ffffff",
    defaultText: "#000000",
    highlightColor: 0x3b82f6, // Blue highlight for selected elements
  },

  // Rotation constants
  rotation: {
    flatTextRotation: -Math.PI / 2, // -90 degrees for flat text orientation
    degreesToRadians: Math.PI / 180, // Conversion factor
  },
} as const;

const ThreeDDesignCanvas = forwardRef<
  ThreeDDesignCanvasRef,
  ThreeDDesignCanvasProps
>(
  (
    {
      backgroundColor,
      backgroundDepth,
      backgroundForm,
      backgroundEnabled = true,
      backgroundWidth = 100,
      backgroundHeight = 60,
      backgroundBorderRadius = 0,
      keychainRingEnabled = false,
      keychainRingSide = "top",
      keychainRingEmbeddingDepth = CANVAS_CONSTANTS.keychainRing
        .embeddingDepthRatio,
      keychainRingSize = 1.0,
      keychainRingThickness = 1.0,
      elements,
      selectedElement,
      selectedCanvas: _selectedCanvas,
      onElementSelect,
      onCanvasSelect,
      onElementUpdate: _onElementUpdate,
      onElementDelete: _onElementDelete,
      onElementDoubleClick: _onElementDoubleClick,
    },
    ref
  ) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<{
      scene: THREE.Scene;
      camera: THREE.PerspectiveCamera;
      renderer: THREE.WebGLRenderer;
      controls?: any;
      backgroundMesh?: THREE.Mesh;
      keychainRingMesh?: THREE.Mesh;
      textMeshes: Map<string, THREE.Mesh>;
      raycaster: THREE.Raycaster;
      mouse: THREE.Vector2;
    } | null>(null);
    const onElementSelectRef = useRef(onElementSelect);
    const onCanvasSelectRef = useRef(onCanvasSelect);
    const hasFramedCameraRef = useRef(false); // Track if we've done initial camera framing
    const prevElementsCountRef = useRef(0); // Track previous element count
    const prevBackgroundEnabledRef = useRef<boolean | undefined>(undefined); // Track previous background enabled state

    // Update refs when callbacks change
    useEffect(() => {
      onElementSelectRef.current = onElementSelect;
      onCanvasSelectRef.current = onCanvasSelect;
    }, [onElementSelect, onCanvasSelect]);

    // Export 3D model functionality (STL)
    const export3DModel = useCallback(async () => {
      if (!sceneRef.current) {
        throw new Error("Scene not initialized");
      }

      const { scene } = sceneRef.current;

      // Use STL exporter for Blender compatibility
      try {
        const { STLExporter } = await import(
          "three/examples/jsm/exporters/STLExporter.js"
        );
        const exporter = new STLExporter();
        const result = exporter.parse(scene);

        // Create blob and download
        const blob = new Blob([result], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `3d-design-${Date.now()}.stl`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting STL:", error);
        throw error;
      }
    }, []);

    // Export 3D model functionality (OBJ)
    const export3DModelOBJ = useCallback(async () => {
      if (!sceneRef.current) {
        throw new Error("Scene not initialized");
      }

      const { scene } = sceneRef.current;

      // Use OBJ exporter
      try {
        const { OBJExporter } = await import(
          "three/examples/jsm/exporters/OBJExporter.js"
        );
        const exporter = new OBJExporter();
        const result = exporter.parse(scene);

        // Create blob and download
        const blob = new Blob([result], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `3d-design-${Date.now()}.obj`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting OBJ:", error);
        throw error;
      }
    }, []);

    // Capture screenshot from current camera view
    const captureScreenshot = useCallback(async (): Promise<string> => {
      if (!sceneRef.current) {
        throw new Error("Scene not initialized");
      }

      const { renderer, scene, camera } = sceneRef.current;

      // Wait a bit to ensure all async operations (like ring creation) are complete
      await new Promise((resolve) =>
        setTimeout(resolve, CANVAS_CONSTANTS.screenshot.renderDelay)
      );

      // Render the scene multiple times to ensure everything is up to date
      renderer.render(scene, camera);
      await new Promise((resolve) => requestAnimationFrame(resolve));
      renderer.render(scene, camera);

      // Capture the canvas as data URL
      const dataURL = renderer.domElement.toDataURL("image/png");
      return dataURL;
    }, []);

    useImperativeHandle(ref, () => ({
      export3DModel,
      export3DModelOBJ,
      captureScreenshot,
    }));

    // Font cache to avoid reloading fonts
    const fontCacheRef = useRef<Map<string, any>>(new Map());

    // Font mapping - map common font families to Three.js available fonts
    // Three.js only has 3 example fonts: helvetiker (sans-serif), gentilis (serif), optimer (monospace)
    // We map all fonts to these 3 based on their characteristics
    const fontUrlMap: Record<string, string> = {
      // Sans-serif fonts -> helvetiker
      Arial:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Helvetica:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Verdana:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Trebuchet MS":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Tahoma:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Roboto:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Open Sans":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Lato: "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Montserrat:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Oswald:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Raleway:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Poppins:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Source Sans Pro":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Ubuntu:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Nunito:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Fira Sans":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "PT Sans":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Work Sans":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Inter:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Comic Sans MS":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Impact:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Dancing Script": "/typeface-fonts/Dancing Script_Regular.json",
      Pacifico: "/typeface-fonts/Pacifico_Regular.json",
      Lobster: "/typeface-fonts/Lobster_Regular.json",
      "Bebas Neue":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Bangers:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Righteous:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Fredoka One":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Comfortaa:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Quicksand:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      Caveat: "/typeface-fonts/Caveat_Regular.json",
      Kalam:
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Permanent Marker":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      "Shadows Into Light": "/typeface-fonts/Shadows Into Light_Regular.json",
      "Indie Flower":
        "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json",
      // Serif fonts -> gentilis
      "Times New Roman":
        "https://threejs.org/examples/fonts/gentilis_regular.typeface.json",
      Georgia:
        "https://threejs.org/examples/fonts/gentilis_regular.typeface.json",
      Palatino:
        "https://threejs.org/examples/fonts/gentilis_regular.typeface.json",
      Garamond:
        "https://threejs.org/examples/fonts/gentilis_regular.typeface.json",
      "Playfair Display":
        "https://threejs.org/examples/fonts/gentilis_regular.typeface.json",
      Merriweather:
        "https://threejs.org/examples/fonts/gentilis_regular.typeface.json",
      "Crimson Text":
        "https://threejs.org/examples/fonts/gentilis_regular.typeface.json",
      // Monospace fonts -> optimer
      "Courier New":
        "https://threejs.org/examples/fonts/optimer_regular.typeface.json",
      "Lucida Console":
        "https://threejs.org/examples/fonts/optimer_regular.typeface.json",
      // Custom local fonts from /public/typeface-fonts/
      Almarai: "/typeface-fonts/Almarai_Regular.json",
      Anton: "/typeface-fonts/Anton_Regular.json",
      "Barlow Condensed": "/typeface-fonts/Barlow Condensed_Regular.json",
      "BBH Bartle": "/typeface-fonts/BBH Bartle_Regular.json",
      Cairo: "/typeface-fonts/Cairo_Regular.json",
      "Edu SA Beginner": "/typeface-fonts/Edu SA Beginner_Regular.json",
      Follow: "/typeface-fonts/Follow.json",
      "Great Vibes": "/typeface-fonts/Great Vibes_Regular.json",
      "Josefin Sans Thin": "/typeface-fonts/Josefin Sans Thin_Regular.json",
      "Lobster Two": "/typeface-fonts/Lobster Two_Regular.json",
      "Noto Sans Symbols": "/typeface-fonts/Noto Sans Symbols_Regular.json",
      Orbitron: "/typeface-fonts/Orbitron_Regular.json",
      Satisfy: "/typeface-fonts/Satisfy_Regular.json",
      "Smooch Sans Thin": "/typeface-fonts/Smooch Sans Thin_Regular.json",
      "Space Grotesk Light": "/typeface-fonts/Space Grotesk Light_Regular.json",
      Tajawal: "/typeface-fonts/Tajawal_Regular.json",
    };

    // Create background geometry based on form
    const createBackgroundGeometry = useCallback(
      async (
        form: "circular" | "rectangular" | "heart" | "auto",
        depth: number,
        elements: DesignElement[],
        width: number,
        height: number,
        borderRadius: number
      ): Promise<THREE.BufferGeometry> => {
        switch (form) {
          case "circular": {
            const radius = Math.min(width, height) / 2;
            return new THREE.CylinderGeometry(
              radius,
              radius,
              depth,
              CANVAS_CONSTANTS.backgroundGeometry.cylinderSegments
            );
          }
          case "rectangular": {
            if (borderRadius > 0) {
              // Use RoundedBoxGeometry for rounded corners
              try {
                const { RoundedBoxGeometry } = await import(
                  "three/examples/jsm/geometries/RoundedBoxGeometry.js"
                );
                return new RoundedBoxGeometry(
                  width,
                  depth,
                  height,
                  CANVAS_CONSTANTS.backgroundGeometry.roundedBoxSegments,
                  borderRadius
                );
              } catch (error) {
                console.warn(
                  "RoundedBoxGeometry not available, using BoxGeometry:",
                  error
                );
                return new THREE.BoxGeometry(width, depth, height);
              }
            } else {
              return new THREE.BoxGeometry(width, depth, height);
            }
          }
          case "heart": {
            if (borderRadius > 0) {
              // Use RoundedBoxGeometry for rounded corners
              try {
                const { RoundedBoxGeometry } = await import(
                  "three/examples/jsm/geometries/RoundedBoxGeometry.js"
                );
                return new RoundedBoxGeometry(
                  width,
                  depth,
                  height,
                  CANVAS_CONSTANTS.backgroundGeometry.roundedBoxSegments,
                  borderRadius
                );
              } catch (error) {
                console.warn(
                  "RoundedBoxGeometry not available, using BoxGeometry:",
                  error
                );
                return new THREE.BoxGeometry(width, depth, height);
              }
            } else {
              // Simplified heart shape using a combination of geometries
              // In production, use a proper heart shape geometry
              return new THREE.BoxGeometry(width, depth, height);
            }
          }
          case "auto": {
            // Auto form - create a copy of the text but wider to give it shape and dimension
            if (elements.length === 0) {
              return new THREE.BoxGeometry(
                CANVAS_CONSTANTS.backgroundGeometry.defaultWidth,
                depth,
                CANVAS_CONSTANTS.backgroundGeometry.defaultHeight
              );
            }

            try {
              const BufferGeometryUtilsModule = await import(
                "three/examples/jsm/utils/BufferGeometryUtils.js"
              );
              const mergeGeometries =
                (BufferGeometryUtilsModule as any).mergeGeometries ||
                (BufferGeometryUtilsModule as any).default?.mergeGeometries;
              if (!mergeGeometries) {
                throw new Error("mergeGeometries not available");
              }
              const { TextGeometry } = await import(
                "three/examples/jsm/geometries/TextGeometry.js"
              );
              const { FontLoader } = await import(
                "three/examples/jsm/loaders/FontLoader.js"
              );

              // Helper to load font
              const loadFontForBackground = async (
                fontFamily: string
              ): Promise<any> => {
                // Check cache first
                if (fontCacheRef.current.has(fontFamily)) {
                  return fontCacheRef.current.get(fontFamily);
                }

                const loader = new FontLoader();

                // Get font URL from map, or determine based on font characteristics
                let fontUrl = fontUrlMap[fontFamily];

                // If font not in map, try to determine category and use appropriate fallback
                if (!fontUrl) {
                  // Check if it's a serif font (contains common serif keywords)
                  const serifKeywords = [
                    "serif",
                    "times",
                    "georgia",
                    "palatino",
                    "garamond",
                    "playfair",
                    "merriweather",
                    "crimson",
                  ];
                  const isSerif = serifKeywords.some((keyword) =>
                    fontFamily.toLowerCase().includes(keyword)
                  );

                  // Check if it's a monospace font
                  const monoKeywords = ["mono", "courier", "console", "code"];
                  const isMono = monoKeywords.some((keyword) =>
                    fontFamily.toLowerCase().includes(keyword)
                  );

                  if (isSerif) {
                    fontUrl = fontUrlMap["Times New Roman"];
                  } else if (isMono) {
                    fontUrl = fontUrlMap["Courier New"];
                  } else {
                    // Default to sans-serif
                    fontUrl = fontUrlMap["Arial"];
                  }
                }

                return new Promise((resolve, reject) => {
                  loader.load(
                    fontUrl,
                    (font) => {
                      fontCacheRef.current.set(fontFamily, font);
                      resolve(font);
                    },
                    undefined,
                    (error) => {
                      console.warn(
                        `Failed to load font ${fontFamily}, using default:`,
                        error
                      );
                      loader.load(
                        fontUrlMap["Arial"],
                        (font) => {
                          fontCacheRef.current.set(fontFamily, font);
                          resolve(font);
                        },
                        undefined,
                        () => reject(new Error("Could not load font"))
                      );
                    }
                  );
                });
              };

              // Calculate center of all text elements for positioning
              let centerX = 0;
              let centerY = 0;
              if (elements.length > 0) {
                let minX = Infinity,
                  maxX = -Infinity;
                let minY = Infinity,
                  maxY = -Infinity;
                elements.forEach((el) => {
                  if (el.type === "text") {
                    minX = Math.min(minX, el.position.x);
                    maxX = Math.max(maxX, el.position.x);
                    minY = Math.min(minY, el.position.y);
                    maxY = Math.max(maxY, el.position.y);
                  }
                });
                if (minX !== Infinity) {
                  centerX = (minX + maxX) / 2;
                  centerY = (minY + maxY) / 2;
                }
              }

              // Create text geometries for all text elements
              const textGeometries: THREE.BufferGeometry[] = [];

              for (const element of elements) {
                if (element.type !== "text" || !element.content) {
                  continue;
                }

                const fontSize =
                  element.style.fontSize ||
                  CANVAS_CONSTANTS.textGeometry.defaultFontSize;
                const fontFamily =
                  element.style.fontFamily ||
                  "var(--store-font-family, 'Inter', sans-serif)";
                const orientation = element.style.textOrientation || "flat";

                // Load font
                const font = await loadFontForBackground(fontFamily);

                // Create text geometry with wider bevel to make it wider than the original text
                // Use bevel based on depth (not font size) to maintain correct depth scaling
                // Use backgroundDepth (same scale as text depth: 1 = 1)
                // Use minimal bevel to avoid adding significant extra depth
                // The wider effect comes from bevelSize (outline width), not bevelThickness (depth)
                const bevelThickness =
                  depth *
                  CANVAS_CONSTANTS.backgroundGeometry.bevelThicknessRatio;
                const bevelSize = Math.max(
                  fontSize *
                    CANVAS_CONSTANTS.backgroundGeometry.bevelSizeFontRatio,
                  depth *
                    CANVAS_CONSTANTS.backgroundGeometry.bevelSizeDepthRatio
                );
                const textGeometry = new TextGeometry(element.content, {
                  font: font,
                  size: fontSize,
                  depth: depth, // Use backgroundDepth - 1 unit of background depth = 1 unit of text depth
                  curveSegments:
                    CANVAS_CONSTANTS.backgroundGeometry.curveSegments,
                  bevelEnabled: true,
                  bevelThickness: bevelThickness, // Small to minimize depth addition
                  bevelSize: bevelSize, // Wider outline for visual effect
                  bevelOffset: CANVAS_CONSTANTS.backgroundGeometry.bevelOffset,
                  bevelSegments:
                    CANVAS_CONSTANTS.backgroundGeometry.bevelSegments, // More segments for smoother bevel
                });

                // Center the geometry first
                textGeometry.computeBoundingBox();
                const boundingBox = textGeometry.boundingBox;
                if (boundingBox) {
                  const centerXGeom =
                    (boundingBox.min.x + boundingBox.max.x) / 2;
                  const centerYGeom =
                    (boundingBox.min.y + boundingBox.max.y) / 2;
                  const centerZGeom =
                    (boundingBox.min.z + boundingBox.max.z) / 2;
                  textGeometry.translate(
                    -centerXGeom,
                    -centerYGeom,
                    -centerZGeom
                  );
                }

                // Apply orientation rotation (same as text elements)
                if (orientation === "flat") {
                  textGeometry.rotateX(
                    CANVAS_CONSTANTS.rotation.flatTextRotation
                  );
                }

                // Position the geometry relative to the center of all elements
                // X: element X position relative to center
                // Z: element Y position relative to center (Y in 2D becomes Z in 3D)
                textGeometry.translate(
                  element.position.x - centerX,
                  0,
                  element.position.y - centerY
                );

                // Note: Element rotation is handled at the mesh level, not geometry level
                // The background will follow the text shape without individual element rotations

                textGeometries.push(textGeometry);
              }

              if (textGeometries.length === 0) {
                return new THREE.BoxGeometry(
                  CANVAS_CONSTANTS.backgroundGeometry.defaultWidth,
                  depth,
                  CANVAS_CONSTANTS.backgroundGeometry.defaultHeight
                );
              }

              // Merge all text geometries into one
              const mergedGeometry = mergeGeometries(textGeometries);

              // Center the merged geometry
              mergedGeometry.computeBoundingBox();
              const mergedBoundingBox = mergedGeometry.boundingBox;
              if (mergedBoundingBox) {
                const centerXGeom =
                  (mergedBoundingBox.min.x + mergedBoundingBox.max.x) / 2;
                const centerYGeom =
                  (mergedBoundingBox.min.y + mergedBoundingBox.max.y) / 2;
                const centerZGeom =
                  (mergedBoundingBox.min.z + mergedBoundingBox.max.z) / 2;
                mergedGeometry.translate(
                  -centerXGeom,
                  -centerYGeom,
                  -centerZGeom
                );
              }

              // Store center in userData for positioning
              (mergedGeometry as any).userData = { centerX, centerY };

              return mergedGeometry;
            } catch (error) {
              console.error("Error creating auto background geometry:", error);
              // Fallback to box geometry if text geometry creation fails
              let minX = Infinity,
                maxX = -Infinity;
              let minY = Infinity,
                maxY = -Infinity;

              elements.forEach((el) => {
                if (el.type === "text" && el.content) {
                  const fontSize =
                    el.style.fontSize ||
                    CANVAS_CONSTANTS.textGeometry.defaultFontSize;
                  const textWidth =
                    el.content.length *
                    fontSize *
                    CANVAS_CONSTANTS.textGeometry.textWidthMultiplier;
                  const textHeight =
                    fontSize *
                    CANVAS_CONSTANTS.textGeometry.textHeightMultiplier;

                  const left = el.position.x - textWidth / 2;
                  const right = el.position.x + textWidth / 2;
                  const top = el.position.y - textHeight / 2;
                  const bottom = el.position.y + textHeight / 2;

                  minX = Math.min(minX, left);
                  maxX = Math.max(maxX, right);
                  minY = Math.min(minY, top);
                  maxY = Math.max(maxY, bottom);
                }
              });

              const padding =
                CANVAS_CONSTANTS.backgroundGeometry.autoFormPadding;
              const width = (maxX - minX) * (1 + padding * 2);
              const height = (maxY - minY) * (1 + padding * 2);

              const finalWidth = Math.max(
                width,
                CANVAS_CONSTANTS.backgroundGeometry.defaultWidth
              );
              const finalHeight = Math.max(
                height,
                CANVAS_CONSTANTS.backgroundGeometry.defaultHeight
              );

              const centerX = (minX + maxX) / 2;
              const centerY = (minY + maxY) / 2;

              const geometry = new THREE.BoxGeometry(
                finalWidth,
                depth,
                finalHeight
              );
              (geometry as any).userData = { centerX, centerY };

              return geometry;
            }
          }
          default: {
            return new THREE.BoxGeometry(width, depth, height);
          }
        }
      },
      []
    );

    // Load font helper
    const loadFont = useCallback(async (fontFamily: string): Promise<any> => {
      // Check cache first
      if (fontCacheRef.current.has(fontFamily)) {
        return fontCacheRef.current.get(fontFamily);
      }

      try {
        const { FontLoader } = await import(
          "three/examples/jsm/loaders/FontLoader.js"
        );
        const loader = new FontLoader();

        // Get font URL from map, or determine based on font characteristics
        let fontUrl = fontUrlMap[fontFamily];

        // If font not in map, try to determine category and use appropriate fallback
        if (!fontUrl) {
          // Check if it's a serif font (contains common serif keywords)
          const serifKeywords = [
            "serif",
            "times",
            "georgia",
            "palatino",
            "garamond",
            "playfair",
            "merriweather",
            "crimson",
          ];
          const isSerif = serifKeywords.some((keyword) =>
            fontFamily.toLowerCase().includes(keyword)
          );

          // Check if it's a monospace font
          const monoKeywords = ["mono", "courier", "console", "code"];
          const isMono = monoKeywords.some((keyword) =>
            fontFamily.toLowerCase().includes(keyword)
          );

          if (isSerif) {
            fontUrl = fontUrlMap["Times New Roman"];
          } else if (isMono) {
            fontUrl = fontUrlMap["Courier New"];
          } else {
            // Default to sans-serif
            fontUrl = fontUrlMap["Arial"];
          }
        }

        return new Promise((resolve, reject) => {
          loader.load(
            fontUrl,
            (font) => {
              fontCacheRef.current.set(fontFamily, font);
              resolve(font);
            },
            undefined,
            (error) => {
              console.warn(
                `Failed to load font ${fontFamily}, using default:`,
                error
              );
              // Try to load default font
              loader.load(
                fontUrlMap["Arial"],
                (font) => {
                  fontCacheRef.current.set(fontFamily, font);
                  resolve(font);
                },
                undefined,
                () => {
                  // If default also fails, reject
                  reject(new Error("Could not load font"));
                }
              );
            }
          );
        });
      } catch (error) {
        console.error("Error loading font:", error);
        throw error;
      }
    }, []);

    // Create text geometry
    const createTextGeometry = useCallback(
      async (element: DesignElement): Promise<THREE.BufferGeometry | null> => {
        if (element.type !== "text" || !element.content) {
          return null;
        }

        const fontSize =
          element.style.fontSize ||
          CANVAS_CONSTANTS.textGeometry.defaultFontSize;
        const depth =
          element.style.textDepth ||
          CANVAS_CONSTANTS.textGeometry.defaultTextDepth;
        const orientation = element.style.textOrientation || "flat";
        const fontFamily =
          element.style.fontFamily ||
          "var(--store-font-family, 'Inter', sans-serif)";

        try {
          const { TextGeometry } = await import(
            "three/examples/jsm/geometries/TextGeometry.js"
          );

          // Load font
          const font = await loadFont(fontFamily);

          // Create text geometry with extrusion
          const textGeometry = new TextGeometry(element.content, {
            font: font,
            size: fontSize,
            depth: depth, // Extrusion depth
            curveSegments: CANVAS_CONSTANTS.textGeometry.curveSegments,
            bevelEnabled: true,
            bevelThickness:
              depth * CANVAS_CONSTANTS.textGeometry.bevelThicknessRatio,
            bevelSize: depth * CANVAS_CONSTANTS.textGeometry.bevelSizeRatio,
            bevelOffset: CANVAS_CONSTANTS.textGeometry.bevelOffset,
            bevelSegments: CANVAS_CONSTANTS.textGeometry.bevelSegments,
          });

          // Center the geometry first
          textGeometry.computeBoundingBox();
          const boundingBox = textGeometry.boundingBox;
          if (boundingBox) {
            const centerX = (boundingBox.min.x + boundingBox.max.x) / 2;
            const centerY = (boundingBox.min.y + boundingBox.max.y) / 2;
            const centerZ = (boundingBox.min.z + boundingBox.max.z) / 2;
            textGeometry.translate(-centerX, -centerY, -centerZ);
          }

          // Apply orientation rotation
          if (orientation === "flat") {
            // Text laying flat on background - rotate 90 degrees around X axis
            textGeometry.rotateX(CANVAS_CONSTANTS.rotation.flatTextRotation);
          }
          // For "vertical" orientation, no rotation needed (text stands vertically)

          // Recompute bounding box after transformations
          textGeometry.computeBoundingBox();

          return textGeometry;
        } catch (error) {
          console.error("Error creating text geometry:", error);
          // Fallback to box geometry if font loading fails
          const textWidth =
            element.content.length *
            fontSize *
            CANVAS_CONSTANTS.textGeometry.textWidthMultiplier;
          const textHeight =
            fontSize * CANVAS_CONSTANTS.textGeometry.textHeightMultiplier;

          if (orientation === "vertical") {
            return new THREE.BoxGeometry(textWidth, textHeight, depth);
          } else {
            return new THREE.BoxGeometry(textWidth, depth, textHeight);
          }
        }
      },
      [loadFont]
    );

    // Initialize Three.js scene
    useEffect(() => {
      const mountElement = mountRef.current;
      if (!mountElement) {
        return;
      }

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xf5f5f5);

      const camera = new THREE.PerspectiveCamera(
        CANVAS_CONSTANTS.camera.fov,
        mountElement.clientWidth / mountElement.clientHeight,
        CANVAS_CONSTANTS.camera.near,
        CANVAS_CONSTANTS.camera.far
      );
      // Position camera to view the scene from an angle
      // Looking down at 45 degrees, positioned to see objects at origin
      camera.position.set(
        CANVAS_CONSTANTS.camera.initialPosition.x,
        CANVAS_CONSTANTS.camera.initialPosition.y,
        CANVAS_CONSTANTS.camera.initialPosition.z
      );
      camera.lookAt(
        CANVAS_CONSTANTS.camera.initialTarget.x,
        CANVAS_CONSTANTS.camera.initialTarget.y,
        CANVAS_CONSTANTS.camera.initialTarget.z
      );

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.toneMappingExposure =
        CANVAS_CONSTANTS.renderer.toneMappingExposure;
      mountElement.appendChild(renderer.domElement);

      // Add lights
      const ambientLight = new THREE.AmbientLight(
        CANVAS_CONSTANTS.lighting.ambientLight.color,
        CANVAS_CONSTANTS.lighting.ambientLight.intensity
      );
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(
        CANVAS_CONSTANTS.lighting.directionalLight.color,
        CANVAS_CONSTANTS.lighting.directionalLight.intensity
      );
      directionalLight.position.set(
        CANVAS_CONSTANTS.lighting.directionalLight.position.x,
        CANVAS_CONSTANTS.lighting.directionalLight.position.y,
        CANVAS_CONSTANTS.lighting.directionalLight.position.z
      );
      directionalLight.castShadow = false;
      scene.add(directionalLight);

      const fillLight = new THREE.DirectionalLight(
        CANVAS_CONSTANTS.lighting.fillLight.color,
        CANVAS_CONSTANTS.lighting.fillLight.intensity
      );
      fillLight.position.set(
        CANVAS_CONSTANTS.lighting.fillLight.position.x,
        CANVAS_CONSTANTS.lighting.fillLight.position.y,
        CANVAS_CONSTANTS.lighting.fillLight.position.z
      );
      scene.add(fillLight);

      const topLight = new THREE.DirectionalLight(
        CANVAS_CONSTANTS.lighting.topLight.color,
        CANVAS_CONSTANTS.lighting.topLight.intensity
      );
      topLight.position.set(
        CANVAS_CONSTANTS.lighting.topLight.position.x,
        CANVAS_CONSTANTS.lighting.topLight.position.y,
        CANVAS_CONSTANTS.lighting.topLight.position.z
      );
      scene.add(topLight);

      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();

      sceneRef.current = {
        scene,
        camera,
        renderer,
        controls: null,
        backgroundMesh: undefined,
        textMeshes: new Map(),
        raycaster,
        mouse,
      };

      // Add orbit controls
      import("three/examples/jsm/controls/OrbitControls.js")
        .then((module) => {
          const { OrbitControls } = module;
          const controls = new OrbitControls(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = CANVAS_CONSTANTS.controls.dampingFactor;
          controls.enablePan = true; // Allow panning to navigate
          controls.minDistance = CANVAS_CONSTANTS.controls.minDistance;
          controls.maxDistance = CANVAS_CONSTANTS.controls.maxDistance;
          controls.target.set(
            CANVAS_CONSTANTS.controls.initialTarget.x,
            CANVAS_CONSTANTS.controls.initialTarget.y,
            CANVAS_CONSTANTS.controls.initialTarget.z
          );
          controls.update();

          if (sceneRef.current) {
            sceneRef.current.controls = controls;
          }
        })
        .catch(() => {
          console.warn("OrbitControls not available");
        });

      // Handle mouse clicks for element selection
      const handleClick = (event: MouseEvent) => {
        if (!sceneRef.current) return;

        const rect = renderer.domElement.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        sceneRef.current.raycaster.setFromCamera(mouse, camera);
        const intersects = sceneRef.current.raycaster.intersectObjects(
          scene.children,
          true
        );

        if (intersects.length > 0) {
          const object = intersects[0].object;
          const elementId = object.userData.elementId;

          // Check if clicked object is the background mesh
          if (object === sceneRef.current.backgroundMesh) {
            // Only select canvas if clicking directly on background
            onCanvasSelectRef.current();
          } else if (elementId) {
            // Clicked on a text element
            onElementSelectRef.current(elementId);
          }
          // If clicked on something else (like lights), do nothing
        }
        // If clicked on empty space, do nothing (don't change selection)
      };

      renderer.domElement.addEventListener("click", handleClick);

      // Animation loop
      let animationId: number;
      const animate = () => {
        animationId = requestAnimationFrame(animate);
        if (sceneRef.current?.controls) {
          sceneRef.current.controls.update();
        }
        if (sceneRef.current) {
          sceneRef.current.renderer.render(
            sceneRef.current.scene,
            sceneRef.current.camera
          );
        }
      };
      animate();

      // Handle resize
      const handleResize = () => {
        if (!mountElement) return;
        camera.aspect = mountElement.clientWidth / mountElement.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mountElement.clientWidth, mountElement.clientHeight);
      };
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        renderer.domElement.removeEventListener("click", handleClick);
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
        if (
          mountElement &&
          renderer.domElement &&
          mountElement.contains(renderer.domElement)
        ) {
          mountElement.removeChild(renderer.domElement);
        }
        // Dispose of all scene objects
        if (sceneRef.current) {
          // Dispose background mesh
          if (sceneRef.current.backgroundMesh) {
            scene.remove(sceneRef.current.backgroundMesh);
            if (sceneRef.current.backgroundMesh.geometry) {
              sceneRef.current.backgroundMesh.geometry.dispose();
            }
            if (sceneRef.current.backgroundMesh.material) {
              if (Array.isArray(sceneRef.current.backgroundMesh.material)) {
                sceneRef.current.backgroundMesh.material.forEach((mat) =>
                  mat.dispose()
                );
              } else {
                sceneRef.current.backgroundMesh.material.dispose();
              }
            }
          }
          // Dispose keychain ring mesh
          if (sceneRef.current.keychainRingMesh) {
            scene.remove(sceneRef.current.keychainRingMesh);
            if (sceneRef.current.keychainRingMesh.geometry) {
              sceneRef.current.keychainRingMesh.geometry.dispose();
            }
            if (sceneRef.current.keychainRingMesh.material) {
              if (Array.isArray(sceneRef.current.keychainRingMesh.material)) {
                sceneRef.current.keychainRingMesh.material.forEach((mat) =>
                  mat.dispose()
                );
              } else {
                sceneRef.current.keychainRingMesh.material.dispose();
              }
            }
          }
          // Dispose text meshes
          sceneRef.current.textMeshes.forEach((mesh) => {
            scene.remove(mesh);
            if (mesh.geometry) mesh.geometry.dispose();
            if (mesh.material) {
              if (Array.isArray(mesh.material)) {
                mesh.material.forEach((mat) => mat.dispose());
              } else {
                mesh.material.dispose();
              }
            }
          });
          // Dispose lights
          scene.children.forEach((child) => {
            if (child instanceof THREE.Light) {
              scene.remove(child);
            }
          });
          // Dispose controls
          if (sceneRef.current.controls) {
            sceneRef.current.controls.dispose();
          }
        }
        renderer.dispose();
        // Clear the ref
        sceneRef.current = null;
      };
    }, []); // Empty deps - only run once on mount

    // Update scene when props change (excluding selectedElement to avoid unnecessary recreations)
    useEffect(() => {
      if (!sceneRef.current) return;

      const { scene } = sceneRef.current;
      let isCancelled = false;

      // Reset camera framing flag only when:
      // 1. Elements are added (count increases) - allows framing when first element is added
      // 2. Background is toggled (enabled/disabled) - allows framing when background is added/removed
      // Do NOT reset on property changes (text content, position, etc.)
      const elementsCountChanged =
        elements.length !== prevElementsCountRef.current;
      const backgroundToggled =
        prevBackgroundEnabledRef.current !== undefined &&
        prevBackgroundEnabledRef.current !== backgroundEnabled;

      if (elementsCountChanged || backgroundToggled) {
        hasFramedCameraRef.current = false;
      }

      // Update refs for next comparison
      prevElementsCountRef.current = elements.length;
      prevBackgroundEnabledRef.current = backgroundEnabled;

      // Remove existing background mesh
      if (sceneRef.current.backgroundMesh) {
        scene.remove(sceneRef.current.backgroundMesh);
        if (sceneRef.current.backgroundMesh.geometry) {
          sceneRef.current.backgroundMesh.geometry.dispose();
        }
        if (sceneRef.current.backgroundMesh.material) {
          if (Array.isArray(sceneRef.current.backgroundMesh.material)) {
            sceneRef.current.backgroundMesh.material.forEach((mat) =>
              mat.dispose()
            );
          } else {
            sceneRef.current.backgroundMesh.material.dispose();
          }
        }
        sceneRef.current.backgroundMesh = undefined;
      }

      // Remove existing keychain ring mesh
      if (sceneRef.current.keychainRingMesh) {
        scene.remove(sceneRef.current.keychainRingMesh);
        if (sceneRef.current.keychainRingMesh.geometry) {
          sceneRef.current.keychainRingMesh.geometry.dispose();
        }
        if (sceneRef.current.keychainRingMesh.material) {
          if (Array.isArray(sceneRef.current.keychainRingMesh.material)) {
            sceneRef.current.keychainRingMesh.material.forEach((mat) =>
              mat.dispose()
            );
          } else {
            sceneRef.current.keychainRingMesh.material.dispose();
          }
        }
        sceneRef.current.keychainRingMesh = undefined;
      }

      // Calculate center of all text elements for positioning (do this once for all objects)
      let centerX = 0;
      let centerZ = 0;
      if (elements.length > 0) {
        let minX = Infinity,
          maxX = -Infinity;
        let minY = Infinity,
          maxY = -Infinity;
        elements.forEach((el) => {
          if (el.type === "text") {
            minX = Math.min(minX, el.position.x);
            maxX = Math.max(maxX, el.position.x);
            minY = Math.min(minY, el.position.y);
            maxY = Math.max(maxY, el.position.y);
          }
        });
        if (minX !== Infinity) {
          centerX = (minX + maxX) / 2;
          centerZ = (minY + maxY) / 2;
        }
      }

      // Create new background mesh only if enabled
      if (backgroundEnabled) {
        (async () => {
          const bgGeometry = await createBackgroundGeometry(
            backgroundForm,
            backgroundDepth,
            elements,
            backgroundWidth,
            backgroundHeight,
            backgroundBorderRadius
          );

          if (isCancelled) {
            bgGeometry.dispose();
            return;
          }

          const bgColorHex = (() => {
            const parsed = parseInt(backgroundColor.replace("#", ""), 16);
            return isNaN(parsed)
              ? parseInt(
                  CANVAS_CONSTANTS.colors.defaultBackground.replace("#", ""),
                  16
                )
              : parsed;
          })();
          const bgMaterial = new THREE.MeshStandardMaterial({
            color: bgColorHex,
          });
          const bgMesh = new THREE.Mesh(bgGeometry, bgMaterial);

          // Position background centered at calculated center (or origin if no elements)
          // backgroundDepth uses same scale as text depth (1 = 1)
          bgMesh.position.set(centerX, backgroundDepth / 2, centerZ);

          // Mark as background mesh for click detection
          bgMesh.userData.isBackground = true;
          bgMesh.castShadow = true;
          bgMesh.receiveShadow = true;

          if (isCancelled) {
            bgGeometry.dispose();
            bgMaterial.dispose();
            return;
          }

          if (!sceneRef.current) {
            bgGeometry.dispose();
            bgMaterial.dispose();
            return;
          }

          scene.add(bgMesh);
          sceneRef.current.backgroundMesh = bgMesh;

          // Create keychain ring right after background is created
          if (keychainRingEnabled && !isCancelled && sceneRef.current) {
            try {
              // Get background bounding box for accurate positioning
              const box = new THREE.Box3().setFromObject(bgMesh);
              const bgSize = box.isEmpty()
                ? {
                    x: backgroundWidth,
                    y: backgroundDepth,
                    z: backgroundHeight,
                  }
                : {
                    x: box.max.x - box.min.x,
                    y: box.max.y - box.min.y,
                    z: box.max.z - box.min.z,
                  };

              // Scale ring size based on background dimensions for better visibility
              // Ring thickness should be based on background depth and always be <= background depth
              const effectiveBgWidth =
                bgSize.x ||
                backgroundWidth ||
                CANVAS_CONSTANTS.backgroundGeometry.defaultWidth;
              const effectiveBgHeight =
                bgSize.z ||
                backgroundHeight ||
                CANVAS_CONSTANTS.backgroundGeometry.defaultHeight;
              const effectiveBgDepth =
                bgSize.y ||
                backgroundDepth ||
                CANVAS_CONSTANTS.textGeometry.defaultTextDepth;

              const scaleFactor =
                Math.max(effectiveBgWidth, effectiveBgHeight) /
                CANVAS_CONSTANTS.keychainRing.scaleFactorDivisor;
              const baseRingRadius = Math.min(
                CANVAS_CONSTANTS.keychainRing.maxRingRadius,
                scaleFactor * CANVAS_CONSTANTS.keychainRing.ringRadiusMultiplier
              );
              const ringRadius =
                baseRingRadius * Math.max(0.1, Math.min(2.0, keychainRingSize));
              const baseRingTubeRadius = Math.min(
                effectiveBgDepth *
                  CANVAS_CONSTANTS.keychainRing.tubeRadiusDepthRatio,
                CANVAS_CONSTANTS.keychainRing.maxTubeRadius
              );
              const ringTubeRadius =
                baseRingTubeRadius *
                Math.max(0.1, Math.min(2.0, keychainRingThickness));
              const ringGeometry = new THREE.TorusGeometry(
                ringRadius,
                ringTubeRadius,
                CANVAS_CONSTANTS.keychainRing.radialSegments,
                CANVAS_CONSTANTS.keychainRing.tubularSegments
              );
              const ringColorHex = (() => {
                const parsed = parseInt(backgroundColor.replace("#", ""), 16);
                return isNaN(parsed)
                  ? parseInt(
                      CANVAS_CONSTANTS.colors.defaultBackground.replace(
                        "#",
                        ""
                      ),
                      16
                    )
                  : parsed;
              })();
              const ringMaterial = new THREE.MeshStandardMaterial({
                color: ringColorHex,
              });
              const ringMesh = new THREE.Mesh(ringGeometry, ringMaterial);

              // Position ring to connect to the background edge
              let ringX = centerX;
              let ringY = effectiveBgDepth / 2;
              let ringZ = centerZ;
              const ringRotationX = CANVAS_CONSTANTS.keychainRing.rotationX;
              const ringRotationY = 0;
              const ringRotationZ = 0;

              switch (keychainRingSide) {
                case "top":
                  // Move ring further inside by embedding it into the model
                  ringZ =
                    centerZ -
                    effectiveBgHeight / 2 +
                    ringTubeRadius * keychainRingEmbeddingDepth;
                  ringY = effectiveBgDepth / 2;
                  break;
                case "bottom":
                  // Move ring further inside by embedding it into the model
                  ringZ =
                    centerZ +
                    effectiveBgHeight / 2 -
                    ringTubeRadius * keychainRingEmbeddingDepth;
                  ringY = effectiveBgDepth / 2;
                  break;
                case "left":
                  // Move ring further inside by embedding it into the model
                  ringX =
                    centerX -
                    effectiveBgWidth / 2 +
                    ringTubeRadius * keychainRingEmbeddingDepth;
                  ringY = effectiveBgDepth / 2;
                  break;
                case "right":
                  // Move ring further inside by embedding it into the model
                  ringX =
                    centerX +
                    effectiveBgWidth / 2 -
                    ringTubeRadius * keychainRingEmbeddingDepth;
                  ringY = effectiveBgDepth / 2;
                  break;
              }

              ringMesh.position.set(ringX, ringY, ringZ);
              ringMesh.rotation.set(
                ringRotationX,
                ringRotationY,
                ringRotationZ
              );
              ringMesh.castShadow = true;
              ringMesh.receiveShadow = true;

              scene.add(ringMesh);
              sceneRef.current.keychainRingMesh = ringMesh;
            } catch (error) {
              console.error("Error creating keychain ring:", error);
            }
          }

          // Frame camera when background is created (if not already framed)
          // This handles both initial load and when background is re-enabled
          if (sceneRef.current.controls && !hasFramedCameraRef.current) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (!sceneRef.current || isCancelled) return;
                const {
                  camera: cam,
                  controls: ctrl,
                  backgroundMesh,
                  textMeshes,
                  keychainRingMesh,
                } = sceneRef.current;
                if (ctrl && backgroundMesh) {
                  const box = new THREE.Box3();
                  box.setFromObject(backgroundMesh);
                  // Include text meshes if they exist
                  textMeshes.forEach((mesh) => {
                    box.expandByObject(mesh);
                  });
                  // Include keychain ring if it exists
                  if (keychainRingMesh) {
                    box.expandByObject(keychainRingMesh);
                  }
                  if (!box.isEmpty()) {
                    const center = box.getCenter(new THREE.Vector3());
                    const size = box.getSize(new THREE.Vector3());
                    const maxDim = Math.max(size.x, size.y, size.z);
                    const distance =
                      maxDim *
                      CANVAS_CONSTANTS.camera.framingDistanceMultiplier;

                    // Position camera at an angle for better 3D view
                    cam.position.set(
                      center.x +
                        distance * CANVAS_CONSTANTS.camera.framingPositionX,
                      center.y +
                        distance * CANVAS_CONSTANTS.camera.framingPositionY,
                      center.z +
                        distance * CANVAS_CONSTANTS.camera.framingPositionZ
                    );
                    cam.lookAt(center);
                    ctrl.target.copy(center);
                    ctrl.update();
                    hasFramedCameraRef.current = true; // Mark as framed
                  }
                }
              });
            });
          }
        })();
      } else if (keychainRingEnabled && sceneRef.current) {
        // Create keychain ring even if background is disabled
        try {
          // Calculate center for positioning
          let centerX = 0;
          let centerZ = 0;
          if (elements.length > 0) {
            let minX = Infinity,
              maxX = -Infinity;
            let minY = Infinity,
              maxY = -Infinity;
            elements.forEach((el) => {
              if (el.type === "text") {
                minX = Math.min(minX, el.position.x);
                maxX = Math.max(maxX, el.position.x);
                minY = Math.min(minY, el.position.y);
                maxY = Math.max(maxY, el.position.y);
              }
            });
            if (minX !== Infinity) {
              centerX = (minX + maxX) / 2;
              centerZ = (minY + maxY) / 2;
            }
          }

          const bgSize = {
            x: backgroundWidth,
            y: backgroundDepth,
            z: backgroundHeight,
          };
          const effectiveBgDepth =
            bgSize.y ||
            backgroundDepth ||
            CANVAS_CONSTANTS.textGeometry.defaultTextDepth;
          const scaleFactor =
            Math.max(bgSize.x, bgSize.z) /
            CANVAS_CONSTANTS.keychainRing.scaleFactorDivisor;
          const baseRingRadius = Math.min(
            CANVAS_CONSTANTS.keychainRing.maxRingRadius,
            scaleFactor * CANVAS_CONSTANTS.keychainRing.ringRadiusMultiplier
          );
          const ringRadius =
            baseRingRadius * Math.max(0.1, Math.min(2.0, keychainRingSize));
          const baseRingTubeRadius = Math.min(
            effectiveBgDepth *
              CANVAS_CONSTANTS.keychainRing.tubeRadiusDepthRatio,
            CANVAS_CONSTANTS.keychainRing.maxTubeRadius
          );
          const ringTubeRadius =
            baseRingTubeRadius *
            Math.max(0.1, Math.min(2.0, keychainRingThickness));
          const ringGeometry = new THREE.TorusGeometry(
            ringRadius,
            ringTubeRadius,
            CANVAS_CONSTANTS.keychainRing.radialSegments,
            CANVAS_CONSTANTS.keychainRing.tubularSegments
          );
          const ringColorHex =
            parseInt(backgroundColor.replace("#", ""), 16) ||
            parseInt(
              CANVAS_CONSTANTS.colors.defaultBackground.replace("#", ""),
              16
            );
          const ringMaterial = new THREE.MeshStandardMaterial({
            color: ringColorHex,
          });
          const ringMesh = new THREE.Mesh(ringGeometry, ringMaterial);

          let ringX = centerX;
          let ringY = Math.max(
            ringRadius,
            CANVAS_CONSTANTS.keychainRing.minRingY
          );
          let ringZ = centerZ;
          const ringRotationX = CANVAS_CONSTANTS.keychainRing.rotationX;
          const ringRotationY = 0;
          const ringRotationZ = 0;

          const effectiveBgWidth =
            bgSize.x ||
            backgroundWidth ||
            CANVAS_CONSTANTS.backgroundGeometry.defaultWidth;
          const effectiveBgHeight =
            bgSize.z ||
            backgroundHeight ||
            CANVAS_CONSTANTS.backgroundGeometry.defaultHeight;

          switch (keychainRingSide) {
            case "top":
              // Move ring further inside by embedding it into the model
              ringZ =
                centerZ -
                effectiveBgHeight / 2 +
                ringTubeRadius * keychainRingEmbeddingDepth;
              break;
            case "bottom":
              // Move ring further inside by embedding it into the model
              ringZ =
                centerZ +
                effectiveBgHeight / 2 -
                ringTubeRadius * keychainRingEmbeddingDepth;
              break;
            case "left":
              // Move ring further inside by embedding it into the model
              ringX =
                centerX -
                effectiveBgWidth / 2 +
                ringTubeRadius * keychainRingEmbeddingDepth;
              break;
            case "right":
              // Move ring further inside by embedding it into the model
              ringX =
                centerX +
                effectiveBgWidth / 2 -
                ringTubeRadius * keychainRingEmbeddingDepth;
              break;
          }

          ringMesh.position.set(ringX, ringY, ringZ);
          ringMesh.rotation.set(ringRotationX, ringRotationY, ringRotationZ);
          ringMesh.castShadow = true;
          ringMesh.receiveShadow = true;

          scene.add(ringMesh);
          sceneRef.current.keychainRingMesh = ringMesh;
        } catch (error) {
          console.error("Error creating keychain ring:", error);
        }
      }

      // Remove existing text meshes
      if (!sceneRef.current) return;
      sceneRef.current.textMeshes.forEach((mesh) => {
        scene.remove(mesh);
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((mat) => mat.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
      if (sceneRef.current) {
        sceneRef.current.textMeshes.clear();
      }

      // Create text meshes for each element
      if (!sceneRef.current) return;
      const sortedElements = [...elements].sort((a, b) => a.zIndex - b.zIndex);

      // Use Promise.all to wait for all async geometry creation
      Promise.all(
        sortedElements.map(async (element) => {
          if (element.type !== "text") return null;
          if (isCancelled || !sceneRef.current) return null;

          const geometry = await createTextGeometry(element);
          if (!geometry || isCancelled || !sceneRef.current) {
            if (geometry) geometry.dispose();
            return null;
          }

          const textColorHex =
            parseInt(
              (
                element.style.color || CANVAS_CONSTANTS.colors.defaultText
              ).replace("#", ""),
              16
            ) || 0x000000;
          const material = new THREE.MeshStandardMaterial({
            color: textColorHex,
          });
          const mesh = new THREE.Mesh(geometry, material);

          // Position based on element position
          // Convert 2D canvas coordinates to 3D space
          // Canvas coordinates: (0,0) is top-left, X increases right, Y increases down
          // 3D coordinates: X increases right, Y increases up, Z increases forward (Y in 2D becomes Z in 3D)
          const orientation = element.style.textOrientation || "flat";
          const fontSize =
            element.style.fontSize ||
            CANVAS_CONSTANTS.textGeometry.defaultFontSize;
          const textDepth =
            element.style.textDepth ||
            CANVAS_CONSTANTS.textGeometry.defaultTextDepth;

          // Calculate center of all elements (same calculation as above)
          let centerX = 0;
          let centerZ = 0;
          if (sortedElements.length > 0) {
            let minX = Infinity,
              maxX = -Infinity;
            let minY = Infinity,
              maxY = -Infinity;
            sortedElements.forEach((el) => {
              if (el.type === "text") {
                minX = Math.min(minX, el.position.x);
                maxX = Math.max(maxX, el.position.x);
                minY = Math.min(minY, el.position.y);
                maxY = Math.max(maxY, el.position.y);
              }
            });
            if (minX !== Infinity) {
              centerX = (minX + maxX) / 2;
              centerZ = (minY + maxY) / 2;
            }
          }

          // Position text relative to center
          // X: element X position relative to center
          // Z: element Y position relative to center (Y in 2D becomes Z in 3D)
          // Y: height above background

          // Use backgroundDepth as base height (same scale as text depth: 1 = 1)
          const baseHeight = backgroundEnabled ? backgroundDepth : 0;

          if (orientation === "flat") {
            // Text laying flat on background
            mesh.position.set(
              element.position.x - centerX,
              baseHeight + textDepth / 2,
              element.position.y - centerZ
            );
          } else {
            // Text standing vertically
            mesh.position.set(
              element.position.x - centerX,
              baseHeight + fontSize / 2,
              element.position.y - centerZ
            );
          }

          // Apply rotation
          if (element.style.rotation) {
            mesh.rotation.y =
              element.style.rotation *
              CANVAS_CONSTANTS.rotation.degreesToRadians;
          }

          mesh.userData.elementId = element.id;
          mesh.castShadow = true;

          if (!isCancelled && sceneRef.current) {
            scene.add(mesh);
            sceneRef.current.textMeshes.set(element.id, mesh);
          } else {
            geometry.dispose();
            material.dispose();
          }

          return mesh;
        })
      )
        .then(() => {
          // Frame camera when elements are added or when scene has objects
          // Always frame if not already framed (allows viewer to frame correctly)
          if (!isCancelled && sceneRef.current) {
            const { camera: cam, controls } = sceneRef.current;
            // Always frame camera when we haven't framed yet (ensures view is never empty)
            // This ensures camera is visible even when background is disabled
            if (controls && !hasFramedCameraRef.current) {
              // Use multiple requestAnimationFrame calls to ensure scene is fully rendered
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  if (!sceneRef.current || isCancelled) return;
                  const box = new THREE.Box3();
                  // Include background and all text meshes
                  if (sceneRef.current.backgroundMesh) {
                    box.expandByObject(sceneRef.current.backgroundMesh);
                  }
                  sceneRef.current.textMeshes.forEach((mesh) => {
                    box.expandByObject(mesh);
                  });
                  // Include keychain ring if it exists
                  if (sceneRef.current.keychainRingMesh) {
                    box.expandByObject(sceneRef.current.keychainRingMesh);
                  }

                  if (!box.isEmpty()) {
                    const center = box.getCenter(new THREE.Vector3());
                    const size = box.getSize(new THREE.Vector3());

                    // Adjust camera to frame the scene with proper distance
                    const maxDim = Math.max(size.x, size.y, size.z);
                    const distance =
                      maxDim *
                      CANVAS_CONSTANTS.camera.framingDistanceMultiplier;

                    // Position camera at an angle for better 3D view
                    cam.position.set(
                      center.x +
                        distance * CANVAS_CONSTANTS.camera.framingPositionX,
                      center.y +
                        distance * CANVAS_CONSTANTS.camera.framingPositionY,
                      center.z +
                        distance * CANVAS_CONSTANTS.camera.framingPositionZ
                    );
                    cam.lookAt(center);
                    controls.target.copy(center);
                    controls.update();
                    hasFramedCameraRef.current = true; // Mark as framed
                  } else {
                    // No objects - set default camera position so view is never empty
                    cam.position.set(
                      CANVAS_CONSTANTS.camera.defaultPosition.x,
                      CANVAS_CONSTANTS.camera.defaultPosition.y,
                      CANVAS_CONSTANTS.camera.defaultPosition.z
                    );
                    cam.lookAt(
                      CANVAS_CONSTANTS.camera.defaultTarget.x,
                      CANVAS_CONSTANTS.camera.defaultTarget.y,
                      CANVAS_CONSTANTS.camera.defaultTarget.z
                    );
                    controls.target.set(
                      CANVAS_CONSTANTS.camera.defaultTarget.x,
                      CANVAS_CONSTANTS.camera.defaultTarget.y,
                      CANVAS_CONSTANTS.camera.defaultTarget.z
                    );
                    controls.update();
                    hasFramedCameraRef.current = true;
                  }
                });
              });
            }
          }
        })
        .catch((error) => {
          if (!isCancelled) {
            console.error("Error creating text meshes:", error);
          }
        });

      return () => {
        isCancelled = true;
      };
    }, [
      backgroundColor,
      backgroundDepth,
      backgroundForm,
      backgroundEnabled,
      backgroundWidth,
      backgroundHeight,
      backgroundBorderRadius,
      keychainRingEnabled,
      keychainRingSide,
      keychainRingEmbeddingDepth,
      keychainRingSize,
      keychainRingThickness,
      elements,
      createBackgroundGeometry,
      createTextGeometry,
    ]);

    // Update highlights when selectedElement changes
    useEffect(() => {
      if (!sceneRef.current) return;

      const { textMeshes } = sceneRef.current;

      // Remove all existing highlights
      textMeshes.forEach((mesh) => {
        // Remove any existing edge lines
        const edgesToRemove: THREE.LineSegments[] = [];
        mesh.children.forEach((child) => {
          if (child instanceof THREE.LineSegments) {
            edgesToRemove.push(child);
          }
        });
        edgesToRemove.forEach((edge) => {
          mesh.remove(edge);
          edge.geometry.dispose();
          if (edge.material instanceof THREE.Material) {
            edge.material.dispose();
          }
        });
      });

      // Add highlight to selected element
      if (selectedElement) {
        const selectedMesh = textMeshes.get(selectedElement);
        if (selectedMesh && selectedMesh.geometry) {
          const edges = new THREE.EdgesGeometry(selectedMesh.geometry);
          const line = new THREE.LineSegments(
            edges,
            new THREE.LineBasicMaterial({
              color: CANVAS_CONSTANTS.colors.highlightColor,
              linewidth: 2,
            })
          );
          selectedMesh.add(line);
        }
      }
    }, [selectedElement]);

    return (
      <div
        ref={mountRef}
        className="w-full h-full"
        style={{
          minHeight: "600px",
          backgroundColor: "#f0f0f0",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          touchAction: "none",
        }}
      />
    );
  }
);

ThreeDDesignCanvas.displayName = "ThreeDDesignCanvas";

export default ThreeDDesignCanvas;
